import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import PageMeta from "../../components/seo/PageMeta";
import Breadcrumbs from "../../components/seo/Breadcrumbs";
import { serviceSchema } from "../../components/seo/schemas";
import {
  Plane, MapPin, Microscope, Dumbbell, Stethoscope, Crown, Video, Briefcase,
  ArrowRight, ArrowUpRight, CheckCircle2, Sparkles, ExternalLink, ChevronDown,
  CalendarClock, FileSignature, UserCheck, Gauge, ShieldCheck, Users, Clock,
  BadgeCheck, HeartHandshake, Hospital, Building2,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  SERVICES content — each child is a full 9-section page            */
/* ------------------------------------------------------------------ */

const SERVICES = {
  "travel-nursing": {
    Icon: Plane,
    label: "Travel Nursing",
    eyebrow: "Contingent · Travel",
    h1: "Travel nurse staffing, nationally deployed.",
    lead: "13-week and longer travel RN contracts across the U.S. — transparent bill-rate math, compact-license fast-tracking, and deployment-ready credentials in under seven days.",
    overview: "Travel Nursing at TRITAL Care® runs on a single operational spine: credentials primary-source verified, pay math on-screen, compact licensure fast-tracked, and one recruiter per clinician. Health systems get dependable, high-quality travel coverage. Clinicians get a transparent, repeatable path from application to paycheck.",
    whoFor: [
      "Acute-care hospitals absorbing census surges, turnover, or open-unit demand",
      "Health systems running core + flex workforce models",
      "SNF, LTAC, and ambulatory operators scaling with travel RN support",
    ],
    useCases: [
      { Icon: Gauge,        t: "Census-surge response",  d: "13–26 week travel coverage deployed within 7 days to stabilize open units during volume spikes." },
      { Icon: Users,        t: "Specialty expansion",     d: "ICU, OR, Cath Lab, L&D, and ED specialty coverage when local talent pools run thin." },
      { Icon: Clock,        t: "Turnover bridge",          d: "Protect quality and overtime exposure while permanent recruiting catches up." },
      { Icon: Hospital,     t: "Greenfield & ramp-up",     d: "Program launches and new-facility ramp-ups with phased travel-heavy staffing." },
    ],
    why: [
      { Icon: BadgeCheck,  t: "Pre-cleared credentials",   d: "Primary-source verified before submission. No re-submission loops, no last-minute gaps." },
      { Icon: Gauge,       t: "Transparent bill-rate math", d: "Every submission shows facility bill rate, clinician pay package, and our margin — no hidden spreads." },
      { Icon: ShieldCheck, t: "Compliance discipline",      d: "HIPAA-aware workflows, tamper-evident audit trail, Joint Commission-ready documentation." },
      { Icon: HeartHandshake, t: "Clinician continuity",    d: "Dedicated recruiters stay with clinicians across assignments — lower attrition, better fit." },
    ],
    compliance: [
      "Primary-source license verification on every placement",
      "Specialty competency checklists scored with years-of-experience markers",
      "BLS, ACLS, PALS, and NIHSS tracked with expiry alerts",
      "Facility-specific onboarding rule sets enforced at submission",
    ],
    related: ["local-per-diem-staffing", "contract-direct-hire", "physician-app-staffing"],
    faqs: [
      { q: "How long are typical assignments?", a: "Standard contracts are 13 weeks with renewal options; 8, 16, and 26-week variants are available." },
      { q: "Do you help with housing?", a: "Yes — non-taxable stipends calculated per GSA rates, or company-arranged housing via our corporate-housing partners." },
      { q: "What states can I work in?", a: "All 50 states. We help clinicians fast-track compact and walk-through licenses where applicable." },
      { q: "Can you integrate with our VMS?", a: "Yes — we're VMS-ready for Aya, Medefis, SHIFT, RightSourcing, and custom instances without custom engineering." },
    ],
  },

  "local-per-diem-staffing": {
    Icon: MapPin,
    label: "Local & Per Diem Staffing",
    eyebrow: "Contingent · Local & PRN",
    h1: "Same-week local and per diem coverage.",
    lead: "Local contract and PRN shifts with credentialed clinicians ready in days, not weeks. Facility-specific rules enforced on every submission, from ICU travel-contract to same-day PRN shift.",
    overview: "Local Staffing and Per Diem at TRITAL Care® solve two related problems with one credentialing engine. Local contracts (4–13 weeks) give facilities in-market depth without housing stipends. Per Diem (shift-by-shift) gives real-time resilience against callouts and census spikes — all governed by your facility's specific rule set.",
    whoFor: [
      "Hospitals and SNFs that want in-market clinicians without housing or tax-home complexity",
      "Schedulers needing reliable callout backfill in top metros",
      "Operators running float pools and internal agencies side-by-side with us",
    ],
    useCases: [
      { Icon: CalendarClock, t: "Same-day PRN coverage",   d: "Post an open shift before 10 AM and fill it the same day in our top 40 metros." },
      { Icon: Clock,         t: "Callout & holiday backfill", d: "Credentialed PRN pool ready for Thanksgiving, Christmas, and high-callout weeks." },
      { Icon: MapPin,        t: "Local contract bridging",   d: "4–13 week W-2 contracts while permanent recruiting closes full-time roles." },
      { Icon: Hospital,      t: "Float-pool supplementation", d: "Extend internal float pools with external PRN coverage governed by the same rule set." },
    ],
    why: [
      { Icon: Gauge,     t: "Fast submission, no re-ask",    d: "Credentialed clinicians in our local pool don't re-credential per shift — one profile, many shifts." },
      { Icon: ShieldCheck, t: "Facility-specific rules",       d: "Your compliance requirements (TB, N95 fit, orientation, unit-specific) enforced automatically at submission." },
      { Icon: BadgeCheck,   t: "Tamper-evident audit trail",    d: "Every shift has a timestamped credential-snapshot for Joint Commission readiness." },
      { Icon: Users,        t: "W-2 or 1099 by preference",     d: "Clinician and facility choose the employment model that fits — we handle multi-state tax and benefits." },
    ],
    compliance: [
      "Facility-specific rule sets encoded per unit",
      "Real-time credential expiry blocking before clinicians can accept",
      "Multi-state payroll and W-2/1099 tax handling",
      "Tamper-evident timestamped credential audit per shift",
    ],
    related: ["travel-nursing", "contract-direct-hire", "allied-health-staffing"],
    faqs: [
      { q: "What's the difference between Local Staffing and Per Diem?", a: "Local contracts are 4–13 week W-2 engagements with defined terms; Per Diem is shift-by-shift with no minimum commitment. Clinicians often mix both." },
      { q: "How quickly can we fill a shift?", a: "Same-week fill is standard; same-day fill is available for most specialties in our top 40 metros." },
      { q: "Do local clinicians pay housing stipends?", a: "No — local contracts are W-2 hourly with overtime, without the housing stipend used in travel contracts." },
      { q: "Can I combine Local + PRN with the same facility?", a: "Yes — single requisition, single credentialing chain, split into modalities per your scheduling patterns." },
    ],
    /* Variant anchor sections for the hub hash-deep-links */
    variantAnchors: [
      { id: "local",    title: "Local Contracts",  body: "4–13 week W-2 local contracts within commuting range. No housing stipends, no tax-home paperwork — just reliable in-market clinical coverage." },
      { id: "per-diem", title: "Per Diem / PRN",   body: "Shift-by-shift credentialed marketplace with facility-configurable rule sets. Same-week fill standard, same-day fill in top metros." },
    ],
  },

  "allied-health-staffing": {
    Icon: Microscope,
    label: "Allied Health",
    eyebrow: "Allied Health",
    h1: "Allied health staffing — lab, imaging, respiratory.",
    lead: "Medical technologists, radiology, respiratory, and laboratory professionals — credentialed, deployed, and supported through assignment completion across inpatient and outpatient settings.",
    overview: "Allied Health at TRITAL Care® covers 20+ clinical specialties with modality-specific competency verification. We source medical technologists, radiology techs (CT, MRI, US, Mammo), respiratory therapists (RT, RRT), and laboratory scientists (MLS, MLT, blood bank) with the exact credentials your setting requires.",
    whoFor: [
      "Acute-care imaging centers balancing modality coverage across shifts",
      "Reference labs and hospital labs filling MLS/MLT coverage",
      "Pulmonary and NICU respiratory therapy departments",
    ],
    useCases: [
      { Icon: Microscope, t: "Modality-specific imaging", d: "CT, MRI, Ultrasound, Mammography, Nuc Med coverage matched to credential and modality." },
      { Icon: Gauge,       t: "Lab volume coverage",       d: "MLS, MLT, and blood bank staffing for reference and hospital lab operations." },
      { Icon: Hospital,    t: "Respiratory coverage",      d: "NICU, adult acute, and home-respiratory therapist placements." },
      { Icon: Users,       t: "Surgical & cardiac tech",    d: "General, ortho, cardiac, and vascular surgical tech staffing." },
    ],
    why: [
      { Icon: BadgeCheck,  t: "Certifications verified",     d: "ASCP, ARRT, CCI, NBRC primary-source verification before placement." },
      { Icon: Gauge,       t: "Modality-matched sourcing",    d: "Matched on the exact modality and specialty — not generic resume-keyword filters." },
      { Icon: ShieldCheck, t: "Compliance discipline",        d: "Joint Commission-aligned credentialing with audit trail per placement." },
      { Icon: HeartHandshake, t: "Nationwide reach",          d: "Allied pools in 45+ states with rapid deployment inside our top metros." },
    ],
    compliance: [
      "ASCP, ARRT, CCI, NBRC primary-source verification",
      "Modality-specific competency validation",
      "Radiation safety and OSHA bloodborne pathogen training current",
      "Facility orientation and unit-specific requirements enforced",
    ],
    related: ["therapy-staffing", "travel-nursing", "local-per-diem-staffing"],
    faqs: [
      { q: "Do you cover all imaging modalities?", a: "Yes — CT, MRI, US, Mammo, Nuc Med, interventional. Matched to credential and ARRT modality." },
      { q: "Can you staff reference labs?", a: "Yes — MLS, MLT, blood bank, and specialty-testing lab professionals." },
      { q: "What about respiratory for NICU?", a: "Yes — NICU respiratory therapists with specialty competency verification and annual re-validation." },
    ],
  },

  "therapy-staffing": {
    Icon: Dumbbell,
    label: "Therapy Staffing",
    eyebrow: "Therapy Services",
    h1: "PT, OT, and SLP staffing across settings.",
    lead: "Physical, occupational, and speech-language pathology placements across acute, inpatient rehab, SNF, home health, outpatient ortho, and school-based settings.",
    overview: "Therapy at TRITAL Care® covers the full rehab continuum. We match PT, OT, SLP, PTA, and COTA clinicians to productivity targets and clinical outcomes — not just headcount fills. Specialty-specific skill checklists and setting-aware matching reduce orientation time and therapist churn.",
    whoFor: [
      "Inpatient rehab units balancing productivity with quality of therapy hours",
      "SNF operators managing Medicare PDPM case-mix",
      "Home health agencies scaling visit-based therapy capacity",
      "School districts and pediatric clinics",
    ],
    useCases: [
      { Icon: Hospital,     t: "Acute rehab coverage",       d: "PT, OT, SLP placements across acute-care and inpatient rehab facilities." },
      { Icon: Users,        t: "SNF & home-health staffing", d: "Visit-based and full-time SNF + home-health therapy coverage." },
      { Icon: Gauge,        t: "Pediatric & school-based",   d: "Pediatric PT, OT, SLP, including school-district contracts and IEP support." },
      { Icon: Clock,        t: "Travel therapy",              d: "13-week travel PT/OT/SLP with weekly stipends and credentialing support." },
    ],
    why: [
      { Icon: BadgeCheck,    t: "Specialty-matched",            d: "We match pediatric, geriatric, ortho, neuro, and cardiopulmonary therapists to the right setting." },
      { Icon: Gauge,         t: "Productivity-aware",            d: "Transparent about treatment-minutes, eval expectations, and documentation tooling." },
      { Icon: ShieldCheck,   t: "Credentialing rigor",            d: "State licensure, NPI, CPR, and facility-specific competencies all verified." },
      { Icon: HeartHandshake, t: "Career-path clinicians",        d: "Therapists with 3+ years average experience — not just entry-level fills." },
    ],
    compliance: [
      "State licensure and NPI primary-source verified",
      "FBI fingerprinting for school-based placements",
      "Facility-specific competencies and unit orientation enforced",
      "Pediatric background checks where required",
    ],
    related: ["allied-health-staffing", "travel-nursing", "contract-direct-hire"],
    faqs: [
      { q: "Do you place therapy across all settings?", a: "Yes — acute, inpatient rehab, SNF, home health, outpatient, pediatric, and school-based settings." },
      { q: "Can you meet SNF PDPM productivity targets?", a: "We set productivity and treatment-minute expectations upfront and source therapists matched to those targets." },
      { q: "What licensing do travel therapists need?", a: "Active state license; we support license applications and PT Compact / NP Compact fast-tracking where applicable." },
    ],
  },

  "physician-app-staffing": {
    Icon: Stethoscope,
    label: "Physician & APP",
    eyebrow: "Provider",
    h1: "Locum tenens and permanent physician + APP placement.",
    lead: "MD, DO, NP, and PA roles — from locum coverage to permanent recruitment with credentialing, privileging, and malpractice coordination handled end-to-end.",
    overview: "Physician & APP staffing at TRITAL Care® runs on a dedicated provider-services team. We support locum coverage (days to months), locum-to-perm transitions, and permanent searches — with the credentialing, privileging, and malpractice-insurance coordination that physician recruitment actually requires.",
    whoFor: [
      "Medical staff offices navigating locum coverage for FMLA, sabbatical, or vacancies",
      "Hospitalist, ED, and anesthesia groups scaling to service-line expansion",
      "APP-led care models in primary care, psychiatry, and specialty practices",
    ],
    useCases: [
      { Icon: Hospital,  t: "Hospitalist coverage",    d: "Locum hospitalist, nocturnist, and swing-shift physician placements for 7-on / 7-off patterns." },
      { Icon: Clock,     t: "ED and surgical specialty", d: "EM, anesthesia, surgery sub-specialty locum coverage, often same-month deployment." },
      { Icon: Users,     t: "APP placement",             d: "NP and PA placements in acute, specialty, and ambulatory settings." },
      { Icon: Gauge,     t: "Locum-to-perm",              d: "Evaluate providers on-assignment and convert to permanent with pre-agreed terms." },
    ],
    why: [
      { Icon: BadgeCheck,   t: "Privileging support",     d: "Malpractice, DEA, state licensing, and privileging applications managed by our credentialing team." },
      { Icon: ShieldCheck,  t: "Malpractice coordination", d: "Occurrence-based malpractice provided on locum engagements; tail options for direct-hire transitions." },
      { Icon: HeartHandshake, t: "Provider continuity",     d: "One provider-services rep per account, no rotating intermediaries." },
      { Icon: Gauge,        t: "Specialty-aware",           d: "Sourced by sub-specialty (electrophysiology, heart-failure, pediatric anesthesia) — not generic physician pools." },
    ],
    compliance: [
      "State licensing, DEA, NPI primary-source verified",
      "Malpractice insurance (occurrence-based) on locum engagements",
      "Privileging applications and credentialing packet support",
      "National Practitioner Data Bank checks",
    ],
    related: ["executive-leadership", "telehealth-staffing", "contract-direct-hire"],
    faqs: [
      { q: "Do you cover all physician specialties?", a: "Yes — hospitalist, ED, anesthesia, primary care, psychiatry, surgery sub-specialties, and most specialty medicine." },
      { q: "Do you carry malpractice for locum MDs?", a: "Yes — occurrence-based malpractice is included on locum engagements with tail options for direct-hire transitions." },
      { q: "Can APPs be placed alongside supervising physicians?", a: "Yes — we coordinate supervising-physician requirements and collaborative-practice arrangements per state." },
    ],
  },

  "executive-leadership": {
    Icon: Crown,
    label: "Executive Leadership",
    eyebrow: "Leadership",
    h1: "Interim healthcare executive leadership.",
    lead: "Interim CNO, CMO, COO, and directors for health systems in transition, restructuring, turnaround, or expansion. 3–12 month engagements with defined operational deliverables.",
    overview: "Executive Leadership at TRITAL Care® places senior clinical operators into interim and direct-hire roles. Our pool includes former CNOs, CMOs, COOs, and clinical directors with 20+ years of operating experience — available for 3–12 month interim engagements or permanent placement.",
    whoFor: [
      "Boards navigating leadership transitions (retirement, unplanned departure, acquisition)",
      "CEOs stabilizing operations during turnaround or financial restructuring",
      "Health systems expanding into new service lines or geographies",
    ],
    useCases: [
      { Icon: Crown,       t: "Interim CNO / CMO",         d: "3–12 month clinical-executive placements with defined operational deliverables and KPIs." },
      { Icon: Gauge,       t: "Turnaround & restructuring", d: "Quality, case management, and peri-op leaders for intensive operational change." },
      { Icon: Users,       t: "Service-line launch",        d: "Directors of new service lines (cardiac, oncology, behavioral health) with go-live experience." },
      { Icon: Building2,   t: "Permanent search",            d: "Retained executive searches for CNO, CMO, COO, CHRO, and VP-level roles." },
    ],
    why: [
      { Icon: BadgeCheck,   t: "Senior operators ready",     d: "Clinical executives with 20+ years of operating experience, ready to deploy in days." },
      { Icon: Gauge,        t: "Outcomes-measured",          d: "Engagements scoped around measurable deliverables, not just seat-filling." },
      { Icon: HeartHandshake, t: "Transition management",     d: "Hand-off plans and knowledge transfer paired with permanent-hire search." },
      { Icon: ShieldCheck,  t: "Governance competency",       d: "Executive references, credential verification, and governance-experience review." },
    ],
    compliance: [
      "Executive references from C-suite peers verified",
      "Credential and licensure verification for clinical executives",
      "Governance competency review for board-reporting roles",
      "Conflict-of-interest and non-compete review",
    ],
    related: ["physician-app-staffing", "contract-direct-hire", "travel-nursing"],
    faqs: [
      { q: "How quickly can you place an interim CNO?", a: "Most interim-CNO placements deploy within 7–14 days; highly-specialized searches (pediatric, academic medical) can take 21 days." },
      { q: "Is search-retainer or contingent available?", a: "Both — retained for C-suite and VP roles; contingent for director-level placements." },
      { q: "Do you pair interim with permanent search?", a: "Yes — interim engagements often run in parallel with a retained permanent search, with transition planning built in." },
    ],
  },

  "telehealth-staffing": {
    Icon: Video,
    label: "Telehealth",
    eyebrow: "Virtual Care",
    h1: "Virtual-care clinician staffing.",
    lead: "Multi-state licensed, HIPAA-trained RNs, physicians, and therapists — deploy-ready for virtual nursing, tele-triage, tele-behavioral health, and tele-ICU.",
    overview: "Telehealth Staffing at TRITAL Care® sources clinicians for the full virtual-care spectrum — from tele-ICU physicians to virtual-nursing admissions teams. Every clinician is multi-state licensed, HIPAA-trained, and onboarded through our remote-ready credentialing tooling.",
    whoFor: [
      "Health systems launching virtual-nursing programs",
      "Telehealth platforms scaling clinician supply",
      "Tele-behavioral health and tele-psychiatry networks",
      "Rural and community hospitals adopting tele-ICU",
    ],
    useCases: [
      { Icon: Video,      t: "Virtual nursing",         d: "Remote admissions, discharges, education, and mentorship for bedside teams." },
      { Icon: Clock,      t: "Tele-triage & urgent care", d: "24/7 staffed virtual-urgent-care coverage with multi-state licensed clinicians." },
      { Icon: Users,      t: "Tele-behavioral health",   d: "Psychiatry, therapy, and crisis services integrated with ED workflows." },
      { Icon: Hospital,   t: "Tele-ICU",                  d: "Critical-care physician and nurse support for community and rural hospitals." },
    ],
    why: [
      { Icon: BadgeCheck,   t: "Multi-state licensed",      d: "Clinicians with 5–15 state compact and walk-through licenses ready for national coverage." },
      { Icon: ShieldCheck,  t: "HIPAA-aware",                d: "Workflows validated against HIPAA requirements for virtual-care and remote data handling." },
      { Icon: Gauge,        t: "Remote-ready onboarding",    d: "Full credentialing, privileging, and IT onboarding executed remotely in days." },
      { Icon: HeartHandshake, t: "Platform integrations",     d: "Experience integrating into Teladoc, Amwell, and custom EHR-based tele workflows." },
    ],
    compliance: [
      "Multi-state licensing verified and tracked",
      "HIPAA training and BAA execution managed per engagement",
      "EHR and tele-platform credentialing handled",
      "Tamper-evident documentation for remote sessions",
    ],
    related: ["physician-app-staffing", "executive-leadership", "travel-nursing"],
    faqs: [
      { q: "How many states do your clinicians cover?", a: "Most virtual clinicians carry 5–15 state licenses via compact and walk-through programs. We can target specific states on request." },
      { q: "Can you integrate with our telehealth platform?", a: "Yes — we've onboarded to Teladoc, Amwell, and custom platforms. We handle IT credentialing and tele-platform training." },
      { q: "Do you staff tele-ICU for community hospitals?", a: "Yes — critical-care physicians and RNs for eICU programs and standalone remote-monitoring operations." },
    ],
  },

  "contract-direct-hire": {
    Icon: Briefcase,
    label: "Contract & Direct Hire",
    eyebrow: "Contract & Permanent",
    h1: "Contract staffing and direct-hire clinical placement.",
    lead: "13–26 week facility-defined contracts with dedicated account management, and permanent direct-hire with competency verification, cultural-fit matching, and a 90-day replacement guarantee.",
    overview: "Contract Staffing and Direct Hire at TRITAL Care® cover the continuum from scoped contingent engagements to permanent placement. Contract runs on facility-defined rate cards and SLAs. Direct Hire runs fee-for-hire or retained with clinical competency verification, reference checks, and cultural-fit assessment.",
    whoFor: [
      "Talent-acquisition leaders shifting from contingent reliance to permanent hiring",
      "Hospitals and SNFs formalizing contingent spend under MSA rate cards",
      "Operators running contract-to-perm evaluation models",
    ],
    useCases: [
      { Icon: FileSignature, t: "13–26 week contracts",       d: "Facility-defined contract engagements with dedicated account management and submission SLAs." },
      { Icon: UserCheck,     t: "Permanent clinical hiring",   d: "Full-time placement with fee-for-hire or retained search models." },
      { Icon: Gauge,         t: "Temp-to-perm conversion",     d: "Evaluate clinicians on assignment and convert to permanent with pre-agreed terms." },
      { Icon: Users,         t: "International placement",      d: "Visa-compliant international nurse and allied-health hiring." },
    ],
    why: [
      { Icon: BadgeCheck,   t: "90-day replacement",          d: "Direct-hire placements carry a 90-day replacement guarantee — no questions asked." },
      { Icon: Gauge,        t: "Competency-verified",          d: "Clinical skills checklist scoring, reference verification, and cultural-fit assessment before submission." },
      { Icon: ShieldCheck,  t: "Salary-band intelligence",      d: "We share market salary bands per role and region — transparent offers close faster." },
      { Icon: HeartHandshake, t: "Contract-to-perm bridging",    d: "Run a clinician on a contract engagement, then convert when performance clears." },
    ],
    compliance: [
      "Reference verification on every direct-hire placement",
      "Clinical competency checklist scored before submission",
      "Background and licensure primary-source verified",
      "International credentialing and visa compliance where applicable",
    ],
    related: ["travel-nursing", "local-per-diem-staffing", "executive-leadership"],
    faqs: [
      { q: "How is the direct-hire fee structured?", a: "Typically a percentage of first-year base salary, payable on start date. Retained searches follow a three-stage fee schedule." },
      { q: "What's the replacement guarantee?", a: "Direct-hire placements carry a 90-day replacement guarantee — if the clinician leaves or is terminated for cause, we replace at no additional fee." },
      { q: "Can I convert a contract clinician to permanent?", a: "Yes — temp-to-perm conversions follow a pre-agreed fee schedule proportional to weeks worked." },
      { q: "Do you place international clinicians?", a: "Yes — visa-compliant international nurse and allied-health hiring with credential equivalency support." },
    ],
    /* Variant anchors for the hub hash-deep-links */
    variantAnchors: [
      { id: "contract",    title: "Contract Staffing", body: "13–26 week facility-defined contracts with dedicated account management, submission-volume SLAs, and transparent rate cards." },
      { id: "direct-hire", title: "Direct Hire",        body: "Permanent clinical placement with competency verification, reference checks, salary-band intelligence, and a 90-day replacement guarantee." },
    ],
  },
};

const ORDER = [
  "travel-nursing","local-per-diem-staffing","allied-health-staffing","therapy-staffing",
  "physician-app-staffing","executive-leadership","telehealth-staffing","contract-direct-hire",
];

const ALL_LABELS = Object.fromEntries(Object.entries(SERVICES).map(([k, v]) => [k, v.label]));

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function ServiceDetail() {
  const { slug } = useParams();
  const [faqOpen, setFaqOpen] = useState(null);
  const svc = SERVICES[slug];
  if (!svc) return <Navigate to="/services" replace />;

  const idx = ORDER.indexOf(slug);
  const prev = idx > 0 ? ORDER[idx - 1] : null;
  const next = idx < ORDER.length - 1 ? ORDER[idx + 1] : null;
  const Icon = svc.Icon;

  /* FAQ JSON-LD schema for SEO */
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: svc.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white" data-testid={`service-detail-${slug}`}>
      <PageMeta
        title={`${svc.label} | TRITAL Care®`}
        description={svc.lead}
        canonical={`https://www.tritalcare.com/services/${slug}`}
        schema={[
          faqSchema,
          serviceSchema({ name: svc.label, description: svc.lead, slug }),
        ]}
      />
      <PublicNav />

      {/* 1 · HERO */}
      <section className="bg-trital-mesh" data-testid="svc-hero">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 pt-14 pb-16 lg:pt-20 lg:pb-20">
          <Breadcrumbs
            className="mb-6"
            trail={[
              { name: "Home", href: "/" },
              { name: "Services", href: "/services" },
              { name: svc.label },
            ]}
          />
          <div className="flex items-start gap-5 max-w-4xl">
            <div className="shrink-0 w-14 h-14 rounded-2xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center">
              <Icon className="w-7 h-7" strokeWidth={1.8} />
            </div>
            <div>
              <div className="overline text-trital-red">{svc.eyebrow}</div>
              <h1 className="mt-3 font-display font-black text-4xl lg:text-[56px] leading-[1.05] text-slate-900 text-balance">{svc.h1}</h1>
              <p className="mt-5 text-[17px] leading-relaxed text-slate-600 max-w-[60ch]">{svc.lead}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register?role=facility" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-navy text-white text-sm font-semibold btn-lift hover:bg-trital-navy-deep" data-testid="svc-cta-request">
                  Request Talent <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                </Link>
                <Link to="/register?role=clinician" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-300 text-trital-navy text-sm font-semibold btn-lift hover:border-trital-navy hover:bg-slate-50" data-testid="svc-cta-find">
                  Find Opportunities
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2 · SERVICE OVERVIEW */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-[1fr_2fr] gap-10" data-testid="svc-overview">
        <div>
          <div className="overline text-trital-red">Service Overview</div>
          <h2 className="mt-3 font-display font-black text-3xl lg:text-[36px] leading-[1.1] text-slate-900 text-balance">A single operational spine.</h2>
        </div>
        <div>
          <p className="text-[16px] leading-[1.7] text-slate-700 max-w-[68ch]">{svc.overview}</p>
        </div>
      </section>

      {/* 2b · VARIANT ANCHOR SECTIONS (only for Local/Per Diem + Contract/Direct Hire) */}
      {svc.variantAnchors && (
        <section className="max-w-[1400px] mx-auto px-5 lg:px-8 pb-4 grid md:grid-cols-2 gap-5" data-testid="svc-variants">
          {svc.variantAnchors.map((v) => (
            <div key={v.id} id={v.id} className="scroll-mt-28 card-premium rounded-2xl p-6 bg-white">
              <div className="overline text-trital-red">Variant</div>
              <h3 className="mt-2 font-display font-bold text-xl text-slate-900">{v.title}</h3>
              <p className="mt-2 text-[14px] text-slate-600 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </section>
      )}

      {/* 3 · WHO THIS SERVICE IS FOR */}
      <section className="bg-trital-mist border-y border-slate-200" data-testid="svc-who-for">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-[1fr_2fr] gap-10">
          <div>
            <div className="overline text-trital-red">Who this is for</div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-[36px] leading-[1.1] text-slate-900 text-balance">Built for these buyers.</h2>
          </div>
          <ul className="space-y-3 max-w-[68ch]">
            {svc.whoFor.map((w) => (
              <li key={w} className="flex items-start gap-3 bg-white rounded-2xl border border-slate-200 p-5">
                <CheckCircle2 className="w-5 h-5 mt-0.5 text-trital-red shrink-0" strokeWidth={1.8} />
                <span className="text-[15px] text-slate-800">{w}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 4 · KEY STAFFING USE CASES */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-20" data-testid="svc-use-cases">
        <div className="max-w-[760px] mb-10">
          <div className="overline text-trital-red">Key use cases</div>
          <h2 className="mt-3 font-display font-black text-3xl lg:text-[36px] leading-[1.1] text-slate-900 text-balance">Where operators put this to work.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {svc.useCases.map((u, i) => (
            <motion.div key={u.t} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.35, delay: i * 0.05 }} className="card-premium card-premium-hover rounded-2xl p-6">
              <div className="w-10 h-10 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center">
                <u.Icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div className="mt-4 font-display font-bold text-[16px] text-slate-900">{u.t}</div>
              <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{u.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 5 · WHY TRITAL CARE */}
      <section className="bg-white border-y border-slate-200" data-testid="svc-why">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-20">
          <div className="max-w-[760px] mb-10">
            <div className="overline text-trital-red">Why TRITAL Care®</div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-[36px] leading-[1.1] text-slate-900 text-balance">Built for clinical quality and operational speed.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {svc.why.map((w, i) => (
              <motion.div key={w.t} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.35, delay: i * 0.05 }} className="card-premium rounded-2xl p-6">
                <div className="w-10 h-10 rounded-xl bg-red-50 text-trital-red flex items-center justify-center">
                  <w.Icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div className="mt-4 font-display font-bold text-[16px] text-slate-900">{w.t}</div>
                <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{w.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 6 · COMPLIANCE & WORKFORCE QUALITY */}
      <section className="bg-trital-mist border-b border-slate-200" data-testid="svc-compliance">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-[1fr_2fr] gap-10">
          <div>
            <div className="overline text-trital-red">Compliance & Quality</div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-[36px] leading-[1.1] text-slate-900 text-balance">Engineered, not checked.</h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {["HIPAA-aware", "SOC 2 aligned", "Joint Commission ready"].map((c) => (
                <span key={c} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-[12px] font-semibold text-trital-navy">
                  <BadgeCheck className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} /> {c}
                </span>
              ))}
            </div>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3 max-w-[68ch]">
            {svc.compliance.map((c) => (
              <li key={c} className="flex items-start gap-2.5 bg-white rounded-2xl border border-slate-200 p-4">
                <ShieldCheck className="w-4 h-4 mt-0.5 text-trital-navy shrink-0" strokeWidth={1.8} />
                <span className="text-[14px] text-slate-800">{c}</span>
              </li>
            ))}
          </ul>
          <div className="lg:col-start-2 lg:col-span-1">
            <Link to="/compliance" className="inline-flex items-center gap-1.5 text-sm font-semibold text-trital-navy hover:gap-2 transition-all" data-testid="svc-compliance-link">
              See our full compliance framework <ArrowUpRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>

      {/* 7 · RELATED SERVICES */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-20" data-testid="svc-related">
        <div className="max-w-[760px] mb-10">
          <div className="overline text-trital-red">Related services</div>
          <h2 className="mt-3 font-display font-black text-3xl lg:text-[36px] leading-[1.1] text-slate-900 text-balance">Keep exploring.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {svc.related.map((rs) => {
            const r = SERVICES[rs];
            if (!r) return null;
            return (
              <Link key={rs} to={`/services/${rs}`} className="group card-premium card-premium-hover rounded-2xl p-6 flex flex-col" data-testid={`svc-related-${rs}`}>
                <div className="w-10 h-10 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center group-hover:bg-trital-navy group-hover:text-white transition-colors">
                  <r.Icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div className="mt-4 font-display font-bold text-[17px] text-slate-900 group-hover:text-trital-navy flex items-center gap-1">
                  {r.label}
                  <ArrowUpRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-trital-red" strokeWidth={1.8} />
                </div>
                <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed flex-1">{r.lead}</p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 8 · FAQ */}
      <section className="bg-trital-mist border-y border-slate-200" data-testid="svc-faqs">
        <div className="max-w-[880px] mx-auto px-5 lg:px-8 py-16 lg:py-20">
          <div className="text-center max-w-[640px] mx-auto">
            <div className="overline text-trital-red">FAQs</div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-[36px] leading-[1.1] text-slate-900 text-balance">Answers to the questions buyers ask.</h2>
          </div>
          <div className="mt-10 space-y-3">
            {svc.faqs.map((f, i) => {
              const open = faqOpen === i;
              return (
                <div key={f.q} className="rounded-2xl bg-white border border-slate-200 shadow-card overflow-hidden" data-testid={`svc-faq-${i}`}>
                  <button onClick={() => setFaqOpen(open ? null : i)} className="w-full text-left px-6 py-5 flex items-center justify-between gap-4" aria-expanded={open}>
                    <span className="font-display font-bold text-[16px] text-slate-900">{f.q}</span>
                    <ChevronDown className={`w-4 h-4 shrink-0 text-trital-navy transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.8} />
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }} className="overflow-hidden">
                        <div className="px-6 pb-5 text-sm text-slate-600 leading-relaxed">{f.a}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Prev / Next */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-10 grid md:grid-cols-2 gap-4">
        {prev ? (
          <Link to={`/services/${prev}`} className="group card-premium card-premium-hover p-6 rounded-2xl" data-testid="svc-prev">
            <div className="overline text-slate-500">Previous</div>
            <div className="mt-2 font-display font-bold text-xl text-slate-900 group-hover:text-trital-navy">← {ALL_LABELS[prev]}</div>
          </Link>
        ) : <div />}
        {next ? (
          <Link to={`/services/${next}`} className="group card-premium card-premium-hover p-6 rounded-2xl md:text-right" data-testid="svc-next">
            <div className="overline text-slate-500">Next</div>
            <div className="mt-2 font-display font-bold text-xl text-slate-900 group-hover:text-trital-navy">{ALL_LABELS[next]} →</div>
          </Link>
        ) : <div />}
      </section>

      {/* 9 · CTA BAND */}
      <section className="relative overflow-hidden bg-trital-navy-mesh text-white" data-testid="svc-cta-band">
        <div className="absolute inset-0 dot-grid opacity-10" aria-hidden="true" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-[1.3fr_1fr] items-center gap-10">
          <div>
            <div className="overline text-trital-red flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" strokeWidth={1.8} /> Ready when you are
            </div>
            <h3 className="mt-3 font-display font-black text-4xl lg:text-[48px] leading-[1.05] text-balance">
              Let's scope your {svc.label.toLowerCase()} need.
            </h3>
            <p className="mt-4 text-white/80 max-w-[58ch]">
              Share your requirements — typical response within one business day.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:min-w-[320px]">
            <Link to="/register?role=facility" className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-white text-slate-900 text-sm font-semibold btn-lift hover:bg-slate-100" data-testid="svc-final-cta-request">
              Request Talent <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-trital-red text-white text-sm font-semibold btn-lift hover:bg-trital-red-deep" data-testid="svc-final-cta-contact">
              Talk to sales <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
            <Link to="/services" className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full border border-white/25 text-white text-sm font-semibold hover:bg-white/10" data-testid="svc-final-cta-back">
              Back to all services
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
